import { FormFeedBack, MainButton, ModalConfirmAction } from '@repo/ui';
import { GetPersonByIdV2UseCaseInterface } from 'domain/usecases';
import { ACTIVE_STATUS } from 'domain/value-objects';
import { useFormik } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { AccessHistoryTable } from 'presentation/app/modules/Person/components/access-history-table';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Row, Col, Input, Label } from 'reactstrap';
import * as Yup from 'yup';

import { usePersonRegisterTab } from '../../../hooks';

interface IPersonChangePasswordSubTab {
  recentLogins: GetPersonByIdV2UseCaseInterface.Result['security']['recentLogin'];
  personStatus: boolean;
}

export const PersonChangePasswordSubTab = ({
  recentLogins,
  personStatus,
}: IPersonChangePasswordSubTab) => {
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState<boolean>(false);
  const [isModalOpen, setIsMOdalOpen] = useState<boolean>(false);
  const [modalClosedByConfirm, setModalClosedByConfirm] = useState(false);

  // Estado visual do switch
  const [localStatus, setLocalStatus] = useState<boolean>(personStatus);

  // Estado para saber se o usuário está tentando mudar
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);

  const { id } = useParams();
  const { currentUser } = useAuth();

  const {
    loadingChangePassword,
    loadingActivateInactivate,
    requestChangePersonPassword,
    requestActivateInactivatePerson,
  } = usePersonRegisterTab();

  function toggleModalBlockedRegister() {
    setIsMOdalOpen(!isModalOpen);
  }

  const handleBlockedRegisterSwitchChange = (checked: boolean) => {
    setLocalStatus(!checked);
    setPendingStatus(!checked);
    toggleModalBlockedRegister();
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      newPassword: '',
      confirmationNewPassword: '',
    },

    validationSchema: Yup.object().shape({
      newPassword: Yup.string()
        .required('Senha é obrigatória')
        .min(8, 'A senha deve conter no mínimo 8 caracteres')
        .max(32, 'A senha deve conter no máximo 32 caracteres')
        .matches(
          /^(?=.*[a-z])/,
          'A senha deve conter pelo menos uma letra minúscula',
        )
        .matches(
          /^(?=.*[A-Z])/,
          'A senha deve conter pelo menos uma letra maiúscula',
        )
        .matches(/^(?=.*\d)/, 'A senha deve conter pelo menos um número')
        .matches(
          /^(?=.*[@#$%&*!])/,
          'A senha deve conter pelo menos um caractere especial (@, #, $, %, &, *, !)',
        ),
      confirmationNewPassword: Yup.string()
        .required('Confirmação de senha é obrigatória')
        .oneOf([Yup.ref('newPassword'), null], 'Senhas não conferem'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        password: values.confirmationNewPassword,
      };

      requestChangePersonPassword(
        {
          id,
          updaterId: currentUser.id,
          ...valuesToSubmit,
        },
        validation,
      );
    },
  });

  const isToActiveOrInactive = pendingStatus
    ? ACTIVE_STATUS.INACTIVE
    : ACTIVE_STATUS.ACTIVE;

  // Sempre sincroniza o visual com o valor real ao receber nova prop
  useEffect(() => {
    setLocalStatus(personStatus);
  }, [personStatus]);

  return (
    <React.Fragment>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Row className="g-3">
          <Col md={4}>
            <Label htmlFor="newPassword" className="form-label">
              Nova Senha
            </Label>
            <div className="position-relative auth-pass-inputgroup">
              <Input
                tabIndex={1}
                name="newPassword"
                placeholder="Digite sua nova senha"
                className="form-control"
                maxLength={32}
                type={showNewPassword ? 'text' : 'password'}
                value={validation.values.newPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.newPassword &&
                    validation.errors.newPassword
                  )
                }
              />
              {validation.touched.newPassword &&
              validation.errors.newPassword ? (
                <FormFeedBack
                  error={validation.errors?.newPassword}
                  isInvalid={
                    !!(
                      validation.touched.newPassword &&
                      validation.errors?.newPassword
                    )
                  }
                />
              ) : null}
              <button
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted me-3"
                type="button"
                id="new-password-addon"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                <i className="ri-eye-fill align-middle"></i>
              </button>
            </div>
          </Col>

          <Col md={4}>
            <Label htmlFor="confirmationNewPassword" className="form-label">
              Confirmação da Nova Senha
            </Label>
            <div className="position-relative auth-pass-inputgroup">
              <Input
                tabIndex={2}
                placeholder="Confirmar senha"
                className="form-control"
                name="confirmationNewPassword"
                value={validation.values.confirmationNewPassword}
                type={showConfirmationPassword ? 'text' : 'password'}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.confirmationNewPassword &&
                    validation.errors.confirmationNewPassword
                  )
                }
              />
              {validation.touched.confirmationNewPassword &&
              validation.errors.confirmationNewPassword ? (
                <FormFeedBack
                  error={validation.errors?.confirmationNewPassword}
                  isInvalid={
                    !!(
                      validation.touched.confirmationNewPassword &&
                      validation.errors?.confirmationNewPassword
                    )
                  }
                />
              ) : null}
              <button
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted me-3"
                type="button"
                id="confirmation-password-addon"
                onClick={() =>
                  setShowConfirmationPassword(!showConfirmationPassword)
                }
              >
                <i className="ri-eye-fill align-middle"></i>
              </button>
            </div>
          </Col>

          <Col className="d-flex justify-content-start align-items-end">
            <MainButton
              type="submit"
              color="primary"
              isLoading={loadingChangePassword}
            >
              Alterar senha
            </MainButton>
          </Col>

          <Col md={12}>
            <div className="form-check form-switch mb-3">
              <Input
                role="switch"
                type="checkbox"
                id="blockedRegister"
                className="form-check-input"
                checked={localStatus}
                onChange={() => {
                  handleBlockedRegisterSwitchChange(localStatus);
                }}
              />
              <Label className="form-check-label" htmlFor="blockedRegister">
                Cadastro Bloqueado
              </Label>
            </div>
          </Col>
        </Row>
      </Form>

      <AccessHistoryTable lastLogins={recentLogins} />

      <ModalConfirmAction
        modalTheme="warning"
        featherIconName="user"
        isOpen={isModalOpen}
        toggle={toggleModalBlockedRegister}
        title={`${pendingStatus ? 'Bloquear' : 'Ativar'} Cadastro?`}
        subtitle={`Tem certeza que deseja ${pendingStatus ? 'bloquear' : 'ativar'} o cadastro?`}
        actionBtnLabel={`${pendingStatus ? 'Bloquear' : 'Ativar'}`}
        loading={loadingActivateInactivate}
        onConfirm={() => {
          setModalClosedByConfirm(true);
          requestActivateInactivatePerson(
            {
              id,
              activeStatus: isToActiveOrInactive,
            },
            () => {
              toggleModalBlockedRegister();
              setPendingStatus(null);
            },
          );
        }}
        onClosed={() => {
          if (!modalClosedByConfirm) {
            setLocalStatus(personStatus);
            setModalClosedByConfirm(null);
          }
          setPendingStatus(null);
          setModalClosedByConfirm(null);
        }}
      />
    </React.Fragment>
  );
};
